import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase/config';
import {
    onAuthStateChanged,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    updateProfile,
    signInAnonymously,
    GoogleAuthProvider,
    signOut
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';

// Niveles de fidelidad basados en puntos acumulados
const calcularNivel = (puntos) => {
    if (puntos >= 5000) return 'Diamante 💎';
    if (puntos >= 2000) return 'Platino 🥇';
    if (puntos >= 1000) return 'Gold ⭐';
    if (puntos >= 300) return 'Silver 🥈';
    return 'Explorador 🌍';
};

const AuthContext = createContext();

// Crea o actualiza el perfil del usuario en Firestore
// - Primera vez: inicializa puntos de bienvenida y nivel
// - Visitas siguientes: solo actualiza ultimoAcceso
const guardarUsuarioEnFirestore = async (firebaseUser) => {
    if (!firebaseUser) {
        console.warn('[Evelux Auth] guardarUsuarioEnFirestore: no hay usuario');
        return;
    }
    try {
        const ref = doc(db, 'users', firebaseUser.uid);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
            // ── NUEVO USUARIO: crear documento completo con puntos de bienvenida ──
            console.log('[Evelux Auth] 🆕 Primer registro, inicializando perfil con puntos de bienvenida');
            await setDoc(ref, {
                nombre: firebaseUser.displayName || '',
                email: firebaseUser.email || '',
                foto: firebaseUser.photoURL || '',
                puntos: 100,               // 🎁 Puntos de bienvenida
                nivel: 'Explorador 🌍',
                creadoEn: serverTimestamp(),
                ultimoAcceso: serverTimestamp(),
            });
        } else {
            // ── USUARIO EXISTENTE: solo actualizar último acceso ──
            await updateDoc(ref, {
                ultimoAcceso: serverTimestamp(),
                // Sincronizar foto y nombre si cambiaron en el proveedor
                ...(firebaseUser.displayName && { nombre: firebaseUser.displayName }),
                ...(firebaseUser.photoURL && { foto: firebaseUser.photoURL }),
            });
        }
        console.log('[Evelux Auth] ✅ Usuario sincronizado en Firestore');
    } catch (error) {
        console.error('[Evelux Auth] ❌ Error sincronizando en Firestore:', error.code, error.message);
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Suscripción al estado de auth de Firebase (Persistencia)
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            setLoading(false);
            // Si hay sesión activa, sincronizar/crear perfil en Firestore
            if (currentUser) {
                await guardarUsuarioEnFirestore(currentUser);
            }
        });

        return () => unsubscribe();
    }, []);

    const loginWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        try {
            const result = await signInWithPopup(auth, provider);
            setUser(result.user);
            // Crear/actualizar el documento del usuario en Firestore al hacer login
            await guardarUsuarioEnFirestore(result.user);
            return result;
        } catch (error) {
            console.error("Error en loginWithGoogle:", error.code, error.message);
            throw error;
        }
    };

    const loginWithEmail = async (email, password) => {
        const result = await signInWithEmailAndPassword(auth, email, password);
        await guardarUsuarioEnFirestore(result.user);
        return result;
    };

    const registerWithEmail = async (email, password, nombre) => {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        // Actualizar displayName en Firebase Auth
        if (nombre) {
            await updateProfile(result.user, { displayName: nombre });
        }
        // Guardar en Firestore con el nombre personalizado
        await guardarUsuarioEnFirestore({ ...result.user, displayName: nombre || result.user.email });
        return result;
    };

    const loginAnonymously = async () => {
        const result = await signInAnonymously(auth);
        // Los anónimos tienen un registro mínimo, sin puntos
        const ref = doc(db, 'users', result.user.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
            await setDoc(ref, {
                nombre: 'Invitado',
                email: '',
                foto: '',
                anonimo: true,
                puntos: 0,
                nivel: 'Invitado',
                creadoEn: serverTimestamp(),
                ultimoAcceso: serverTimestamp(),
            });
        } else {
            await updateDoc(ref, { ultimoAcceso: serverTimestamp() });
        }
        return result;
    };

    // ─── SUMAR PUNTOS ───────────────────────────────────────────────────────────
    // Usar desde cualquier parte de la app: const { agregarPuntos } = useAuth();
    // Ejemplo: await agregarPuntos(50, 'Reserva completada')
    const agregarPuntos = async (cantidad, motivo = '') => {
        if (!auth.currentUser || auth.currentUser.isAnonymous) return;
        const ref = doc(db, 'users', auth.currentUser.uid);
        const nuevoPuntaje = ((await getDoc(ref)).data()?.puntos || 0) + cantidad;
        const nuevoNivel = calcularNivel(nuevoPuntaje);
        await updateDoc(ref, {
            puntos: increment(cantidad),
            nivel: nuevoNivel,
            ...(motivo && { ultimaActividad: motivo }),
        });
        console.log(`[Evelux Rewards] +${cantidad} pts → ${motivo || 'sin motivo'}`);
    };

    const logout = () => signOut(auth);

    return (
        <AuthContext.Provider value={{ user, loginWithGoogle, loginWithEmail, registerWithEmail, loginAnonymously, logout, loading, agregarPuntos }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);